import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Plus, Edit, Trash2, Play, Pause, RotateCcw, Clock, AlertCircle, CheckCircle, Loader2, Settings, Globe, Calendar, RefreshCw, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface ScrapeTask {
  id: string;
  name: string;
  description?: string;
  task_type: string;
  target_url: string;
  selector_rules: any;
  schedule_config: any;
  priority: number;
  max_retries: number;
  timeout_seconds: number;
  use_session: boolean;
  session_profile_id?: string;
  status: string;
  locked_by?: string;
  locked_at?: string;
  retry_count: number;
  last_error?: string;
  next_run_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  scrape_session_profiles?: {
    id: string;
    profile_name: string;
    platform: string;
  };
}

interface SessionProfile {
  id: string;
  profile_name: string;
  platform: string;
  is_active: boolean;
}

interface FormData {
  name: string;
  description: string;
  task_type: string;
  target_url: string;
  selector_rules: string;
  schedule_config: string;
  priority: number;
  max_retries: number;
  timeout_seconds: number;
  use_session: boolean;
  session_profile_id: string;
}

const ScrapeTaskManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ScrapeTask[]>([]);
  const [sessionProfiles, setSessionProfiles] = useState<SessionProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<ScrapeTask | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    task_type: 'single',
    target_url: '',
    selector_rules: '{}',
    schedule_config: '{}',
    priority: 5,
    max_retries: 3,
    timeout_seconds: 300,
    use_session: false,
    session_profile_id: ''
  });

  const taskTypes = [
    { value: 'single', label: '单次任务', icon: Play, color: 'blue' },
    { value: 'periodic', label: '周期任务', icon: RotateCcw, color: 'green' },
    { value: 'scheduled', label: '定时任务', icon: Clock, color: 'purple' }
  ];

  const taskStatuses = [
    { value: 'pending', label: '待执行', icon: Clock, color: 'yellow' },
    { value: 'processing', label: '执行中', icon: Loader2, color: 'blue' },
    { value: 'completed', label: '已完成', icon: CheckCircle, color: 'green' },
    { value: 'failed', label: '失败', icon: AlertCircle, color: 'red' },
    { value: 'paused', label: '已暂停', icon: Pause, color: 'gray' }
  ];

  useEffect(() => {
    fetchTasks();
    fetchSessionProfiles();
  }, []);

  // 当筛选条件改变时重新获取数据
  useEffect(() => {
    if (currentPage === 1) {
      fetchTasks(1, pageSize);
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, typeFilter, pageSize]);

  // 当页码改变时获取数据
  useEffect(() => {
    fetchTasks(currentPage, pageSize);
  }, [currentPage]);

  const fetchSessionProfiles = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=session_list&is_active=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setSessionProfiles(result.data.data || []);
      }
    } catch (error) {
      console.error('获取会话配置失败:', error);
    }
  };

  const fetchTasks = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=task_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page,
          limit,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          task_type: typeFilter !== 'all' ? typeFilter : undefined,
          search: searchTerm || undefined
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setTasks(result.data.data || []);
        setTotalCount(result.data.pagination?.total || 0);
        setTotalPages(result.data.pagination?.totalPages || 0);
        setCurrentPage(result.data.pagination?.page || page);
      } else {
        console.error('获取采集任务失败:', result.error);
        toast.error('获取采集任务失败');
      }
    } catch (error) {
      console.error('获取采集任务失败:', error);
      toast.error('获取采集任务失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      // 验证JSON格式
      let selectorRules, scheduleConfig;
      try {
        selectorRules = JSON.parse(formData.selector_rules);
        scheduleConfig = JSON.parse(formData.schedule_config);
      } catch (error) {
        toast.error('选择器规则或调度配置不是有效的JSON格式');
        return;
      }

      const requestData = {
        name: formData.name,
        description: formData.description,
        task_type: formData.task_type,
        target_url: formData.target_url,
        selector_rules: selectorRules,
        schedule_config: scheduleConfig,
        priority: formData.priority,
        max_retries: formData.max_retries,
        timeout_seconds: formData.timeout_seconds,
        use_session: formData.use_session,
        session_profile_id: formData.use_session ? formData.session_profile_id : null
      };

      if (editingTask) {
        // 更新任务
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=task_update`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: editingTask.id,
            ...requestData
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('任务更新成功');
      } else {
        // 创建任务
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=task_create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error);
        }

        toast.success('任务创建成功');
      }

      await fetchTasks();
      setShowForm(false);
      setEditingTask(null);
      resetForm();
    } catch (error) {
      console.error('保存任务失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`确定要删除任务「${name}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=task_delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('任务删除成功');
      await fetchTasks();
    } catch (error) {
      console.error('删除任务失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleStatusChange = async (task: ScrapeTask, newStatus: string) => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=task_update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: task.id,
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(`任务状态已更新为${getStatusInfo(newStatus).label}`);
      await fetchTasks();
    } catch (error) {
      console.error('更新任务状态失败:', error);
      toast.error('状态更新失败：' + (error as Error).message);
    }
  };

  const handleEdit = (task: ScrapeTask) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      description: task.description || '',
      task_type: task.task_type,
      target_url: task.target_url,
      selector_rules: JSON.stringify(task.selector_rules, null, 2),
      schedule_config: JSON.stringify(task.schedule_config, null, 2),
      priority: task.priority,
      max_retries: task.max_retries,
      timeout_seconds: task.timeout_seconds,
      use_session: task.use_session,
      session_profile_id: task.session_profile_id || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      task_type: 'single',
      target_url: '',
      selector_rules: '{}',
      schedule_config: '{}',
      priority: 5,
      max_retries: 3,
      timeout_seconds: 300,
      use_session: false,
      session_profile_id: ''
    });
  };

  // 移除客户端过滤，因为现在在服务端进行过滤和分页
  const filteredTasks = tasks;

  const getTypeInfo = (type: string) => {
    return taskTypes.find(t => t.value === type) || taskTypes[0];
  };

  const getStatusInfo = (status: string) => {
    return taskStatuses.find(s => s.value === status) || taskStatuses[0];
  };

  const handleViewData = (taskId: string, taskName: string) => {
    navigate(`/admin/scrape/data/${taskId}`, {
      state: { taskName }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Settings className="w-6 h-6 mr-2 text-blue-600" />
              采集任务管理
            </h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => fetchTasks()}
                disabled={loading}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                title="刷新任务列表"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </button>
              <button
                onClick={() => {
                  setEditingTask(null);
                  resetForm();
                  setShowForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                添加任务
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索任务名称、描述或URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              {taskStatuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部类型</option>
              {taskTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* 任务列表 - 表格格式 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    任务信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    类型/状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    目标URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    配置
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task) => {
                  const typeInfo = getTypeInfo(task.task_type);
                  const statusInfo = getStatusInfo(task.status);
                  const TypeIcon = typeInfo.icon;
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <tr key={task.id} className="hover:bg-gray-50">
                      {/* 任务信息 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`p-2 rounded-lg bg-${typeInfo.color}-100 mr-3 flex-shrink-0`}>
                            <TypeIcon className={`w-4 h-4 text-${typeInfo.color}-600`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate" title={task.name}>
                              {task.name}
                            </div>
                            {task.description && (
                              <div className="text-sm text-gray-500 max-w-[200px] truncate" title={task.description}>
                                {task.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      {/* 类型/状态 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${typeInfo.color}-100 text-${typeInfo.color}-800`}>
                            {typeInfo.label}
                          </span>
                          <br />
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                            <StatusIcon className={`w-3 h-3 mr-1 ${statusInfo.value === 'processing' ? 'animate-spin' : ''}`} />
                            {statusInfo.label}
                          </span>
                        </div>
                      </td>
                      
                      {/* 目标URL */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 flex items-center">
                          <Globe className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="max-w-[250px] truncate" title={task.target_url}>{task.target_url}</span>
                        </div>
                      </td>
                      
                      {/* 配置 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>优先级: {task.priority}</div>
                          <div>重试: {task.retry_count}/{task.max_retries}</div>
                          <div>超时: {task.timeout_seconds}s</div>
                        </div>
                      </td>
                      
                      
                      {/* 创建时间 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(task.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      
                      {/* 操作 */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {task.status === 'pending' && (
                            <button
                              onClick={() => handleStatusChange(task, 'processing')}
                              className="text-green-600 hover:text-green-900"
                              title="开始执行"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {task.status === 'processing' && (
                            <button
                              onClick={() => handleStatusChange(task, 'paused')}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="暂停执行"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {(task.status === 'paused' || task.status === 'failed') && (
                            <button
                              onClick={() => handleStatusChange(task, 'pending')}
                              className="text-blue-600 hover:text-blue-900"
                              title="重新执行"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          {task.status === 'completed' && (
                            <button
                              onClick={() => handleStatusChange(task, 'pending')}
                              className="text-green-600 hover:text-green-900"
                              title="重新执行"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewData(task.id, task.name)}
                            className="text-green-600 hover:text-green-900"
                            title="查看数据"
                          >
                            <Database className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(task)}
                            className="text-blue-600 hover:text-blue-900"
                            title="编辑"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(task.id, task.name)}
                            className="text-red-600 hover:text-red-900"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 分页控件 */}
          {totalCount > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  显示 {Math.min((currentPage - 1) * pageSize + 1, totalCount)} - {Math.min(currentPage * pageSize, totalCount)} 条，共 {totalCount} 条
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value={10}>10条/页</option>
                  <option value={20}>20条/页</option>
                  <option value={50}>50条/页</option>
                  <option value={100}>100条/页</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  首页
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  末页
                </button>
              </div>
            </div>
          )}

          {filteredTasks.length === 0 && !loading && (
            <div className="text-center py-12">
              <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {totalCount === 0 ? '暂无采集任务数据' : '当前筛选条件下无数据'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTask ? '编辑采集任务' : '添加采集任务'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      任务名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入任务名称"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      任务类型
                    </label>
                    <select
                      value={formData.task_type}
                      onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      {taskTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    目标URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    任务描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入任务描述"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      优先级 (1-10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      最大重试次数
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={formData.max_retries}
                      onChange={(e) => setFormData({ ...formData, max_retries: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      超时时间 (秒)
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="3600"
                      value={formData.timeout_seconds}
                      onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.use_session}
                      onChange={(e) => setFormData({ ...formData, use_session: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">使用会话配置</span>
                  </label>
                  {formData.use_session && (
                    <select
                      value={formData.session_profile_id}
                      onChange={(e) => setFormData({ ...formData, session_profile_id: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">选择会话配置</option>
                      {sessionProfiles.map(profile => (
                        <option key={profile.id} value={profile.id}>
                          {profile.profile_name} ({profile.platform})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    选择器规则 (JSON格式)
                  </label>
                  <textarea
                    value={formData.selector_rules}
                    onChange={(e) => setFormData({ ...formData, selector_rules: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='{"container": ".item", "fields": {"title": "h2", "content": "p"}}'
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    调度配置 (JSON格式)
                  </label>
                  <textarea
                    value={formData.schedule_config}
                    onChange={(e) => setFormData({ ...formData, schedule_config: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='{"interval": "1h", "cron": "0 */1 * * *"}'
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingTask(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapeTaskManagement;